import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import SearchBar from "../OpenSearch/SearchBar";
import Legends from "../OpenSearch/Legends";
import CustomPagination from "../../Components/Pagination";
import constants from "../../Utils/constants";
import { formatRoute } from "react-router-named-routes";
import "./Style/GlobalSearchResult.scss";
import CustomDropdownList from "../../Components/CustomDropdownList";
import { OpenSearchService } from "./Service/OpenSearchService";
import CustomEllipsis from "../../Utils/CustomEllipsis";
import moment from "moment";
import { getReportTypeLabel, redirectToReport, renderIcon } from "./Helper";
import {
  FILTER_BY_TYPE_OPTIONS,
  PUBLISHED_DATE_OPTIONS,
  REPORT_TYPE_OPTIONS,
} from "./Constant";
import Toast from "../../Utils/Toast";
import { HistoricalFileService } from "../../Services/HistoricalFiles";
import { debounce, delay } from "lodash";
import gaEvent from "../../Utils/GAEvents";
import CustomDatePicker from "../../Components/CustomDatePicker";

const GlobalSearchResult = () => {
  const history = useHistory();
  const location = useLocation();

  const [searchInput, setSearchInput] = useState("");
  const [options, setOptions] = useState(REPORT_TYPE_OPTIONS);
  const selectedReportType = options.find((opt) => opt.is_selected);
  const [update, setUpdate] = useState(true);

  const [publishedDateOptions, setPublishedDateOptions] = useState([
    ...PUBLISHED_DATE_OPTIONS,
    { label: "Custom Date", value: "custom", is_selected: false },
  ]);
  const [selectedDateFilter, setSelectedDateFilter] = useState("any-time");

  const [filterByTypeOptions, setFilterByTypeOptions] = useState(
    FILTER_BY_TYPE_OPTIONS
  );
  const [filterByTypeFilter, setFilterByTypeFilter] = useState(null);

  const [resultCount, setResultCount] = useState(0);
  const [searchResult, setSearchResult] = useState([]);
  const [pagination, setPagination] = useState({
    pageNo: constants.PAGINATION.DEFAULT_PAGE_NO,
    pageSize: constants.PAGINATION.DEFAULT_PAGE_SIZE,
    totalPages: 10,
  });

  const [searchSuggestion, setSearchSuggestion] = useState([]);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showSearchSuggestion, setShowSearchSuggestion] = useState(false);
  const [finalSearchTerm, setFinalSearchTerm] = useState("");

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startdate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get("q");

    if (value) {
      setSearchInput(decodeURIComponent(value));
      setFinalSearchTerm(decodeURIComponent(value));
    }

    const { report_type, published_date, file_filter } = location.state || {};

    if (report_type) {
      const updatedOptions = options.map((opt) => ({
        ...opt,
        is_selected: opt.id === report_type.id,
      }));
      setOptions(updatedOptions);
    }

    if (published_date) {
      const updatedOptions = publishedDateOptions.map((option) => ({
        ...option,
        is_selected: option.value === published_date,
      }));

      setPublishedDateOptions(updatedOptions);
      setSelectedDateFilter(published_date);
    }

    if (file_filter) {
      const updatedOptions = filterByTypeOptions.map((option) => ({
        ...option,
        is_selected: option.value === file_filter,
      }));

      setFilterByTypeOptions(updatedOptions);
      setFilterByTypeFilter(file_filter);
    }
  }, [location.search, location.state]);

  useEffect(() => {
    if (searchInput?.length >= 3 && update) {
      debouncedFetchSuggestions(searchInput);
    } else {
      if (searchInput?.length === 0) {
        fetchSearchHistory();
        setFinalSearchTerm("");
        setUpdate(true);
        setSearchResult([]);
        setResultCount(0);
      }
    }
  }, [
    searchInput,
    selectedReportType,
    selectedDateFilter,
    filterByTypeFilter,
    pagination,
  ]);

  useEffect(() => {
    const isCustomDateSelected = selectedDateFilter === "custom";
    const hasValidCustomDates = startdate && endDate;

    if (finalSearchTerm && finalSearchTerm.length >= 3) {
      if (isCustomDateSelected && hasValidCustomDates) {
        getSearchResults(finalSearchTerm);
      } else if (!isCustomDateSelected) {
        getSearchResults(finalSearchTerm);
      }
    } else {
      setSearchResult([]);
    }
  }, [
    finalSearchTerm,
    selectedReportType,
    selectedDateFilter,
    startdate,
    endDate,
    filterByTypeFilter,
    pagination,
  ]);

  const debouncedFetchSuggestions = useMemo(
    () =>
      debounce((input) => {
        const body = { search: input };
        OpenSearchService.fetchSearchSuggestions(body)
          .then((res) => {
            const suggestions = [...new Set(res?.data?.highlights)];
            setSearchSuggestion(suggestions);
          })
          .catch((error) => console.error("Suggestion fetch error:", error));
      }, 800),
    []
  );

  // api for get recentlyViewed data
  const getSearchResults = async (searchInputData) => {
    let body = {
      search: searchInputData,
      page: pagination.pageNo,
      pageSize: pagination.pageSize,
    };

    if (selectedReportType.value) {
      body.type = [selectedReportType.value];
    }

    if (selectedDateFilter === "custom") {
      if (startdate && endDate) {
        body.startDate = startdate;
        body.endDate = endDate;
      }
    } else if (selectedDateFilter) {
      body.publishedAt = selectedDateFilter;
    }

    if (filterByTypeFilter && filterByTypeFilter !== "all") {
      body.method = filterByTypeFilter;
    }

    await OpenSearchService.getSearchResults(body)
      .then((result) => {
        const responseData = result?.data?.data;
        setSearchResult(responseData || []);
        setResultCount(result?.data?.meta?.total || 0);
        setUpdate(false);
        let url = `/search`;
        if (searchInputData) {
          url = url + `?value=${searchInputData}`;
        }
        history.push(url);
      })
      .catch((error) => {
        console.error("Error fetching getSearchResults:", error);
        setUpdate(false);
      });
  };

  // Fetch search history
  const fetchSearchHistory = async () => {
    try {
      const body = {
        page_size: 0,
        page: 0,
        sort_order: "desc",
        sort_column: "id",
      };
      const res = await OpenSearchService.fetchSearchHistory(body);
      const keywords = [...new Set(res?.data?.data?.map((ele) => ele.keyword))];
      setSearchHistory(keywords);
      setSearchSuggestion(keywords);
    } catch (error) {
      console.error("Error fetching search history:", error);
    }
  };

  // Handle selection of report type from dropdown
  const handleCompanyResearchOptionsChange = (selectedItem) => {
    const updatedOptions = options.map((opt) => ({
      ...opt,
      is_selected: opt.id === selectedItem.id,
    }));
    setOptions(updatedOptions);
    setUpdate(true);
  };

  const handleFilterByTypeChange = (selectedOption) => {
    const updatedOptions = filterByTypeOptions.map((option) => ({
      ...option,
      is_selected: option.value === selectedOption.value,
    }));

    setFilterByTypeOptions(updatedOptions);
    setFilterByTypeFilter(selectedOption.value);
    setUpdate(true);
  };

  const handleLastUpdatedChange = (selectedOption) => {
    const updatedOptions = publishedDateOptions.map((option) => ({
      ...option,
      is_selected: option.value === selectedOption.value,
    }));

    setPublishedDateOptions(updatedOptions);
    setSelectedDateFilter(selectedOption.value);
    if (selectedOption.label === "Custom Date") {
      setShowDatePicker(true);
      setSelectedDateFilter("custom");
    } else {
      setShowDatePicker(false);
      setStartDate();
      setEndDate();
    }
  };

  const goBackToDashboard = () => {
    history.push(formatRoute(constants.APPLICATION_ROUTE.DASHBOARD_LIST.ROUTE));
  };

  const resetSearchInput = () => {
    setSearchInput("");
    setUpdate(true);
  };

  const totalPages = () => {
    if (resultCount === 0) {
      return 1;
    }
    const checkingNumber =
      parseInt(Math.ceil(resultCount / pagination.pageSize)) || 0;
    return checkingNumber;
  };

  const handlePageSize = (e) => {
    setPagination({
      ...pagination,
      pageSize: e.target.value || pagination.pageSize,
      pageNo: 1,
    });
    setUpdate(true);
  };

  const handlePage = (input) => {
    setPagination({
      ...pagination,
      pageNo: input,
    });
    setUpdate(true);
  };

  const handleResetPage = () => {
    setSelectedDateFilter("any-time");
    setPublishedDateOptions(PUBLISHED_DATE_OPTIONS);
    setOptions(REPORT_TYPE_OPTIONS);
    setFilterByTypeOptions(FILTER_BY_TYPE_OPTIONS);
    setFilterByTypeFilter("");
    setUpdate(true);
  };

  /**
   * Checks if the user has access to a specific report file and redirects if access is granted.
   * @param {Object} item - Contains report details like type and mapping ID.
   */

  const checkFileAccess = (item) => {
    let accessPayload = {
      resource_type: item?.type,
    };
    if (item.type === "company") {
      accessPayload.mapping_id = item?.company_id;
      accessPayload.segment_id = item?.segment_id;
    } else {
      accessPayload.mapping_id = item?.mapping_id;
    }

    // Step 2: Call API to check access
    OpenSearchService.checkResourceAccess(accessPayload)
      .then((res) => {
        const hasFileAccess = Boolean(res?.data?.has_access);

        if (hasFileAccess) {
          // Step 3: Save recently viewed data
          const recently_viewed_body = {
            ...item,
            subcategory_title:
              item.subcategory_title?.trim() === ""
                ? item.subcategory_name
                : item.subcategory_title,
            published_at: item.created_at,
          };

          return OpenSearchService.saveRecentlyViewedData(recently_viewed_body)
            .then((res) => {
              if (res.status === 200) {
                // Step 4: Redirect to report
                const path = redirectToReport(item);
                window.open(path, "_blank", "noopener,noreferrer");
              }
            })
            .catch((saveError) => {
              console.error("Failed to save recently viewed:", saveError);
            });
        } else {
          // If no access, show info toast
          Toast("You don't have access to this report.", "INFO");
        }
      })
      .catch((error) => {
        // Catch any error in access check flow
        Toast(
          error?.data?.error || constants.ERROR.SOMETHING_WENT_WRONG,
          "error"
        );
      });
  };

  const handleDownloadFile = async (data) => {
    // Step 1: Prepare payload for access check
    const accessPayload = {
      resource_type: data?.type,
      mapping_id: data?.mapping_id,
    };

    // Step 2: Call API to check access and download historical file
    await OpenSearchService.checkResourceAccess(accessPayload)
      .then((res) => {
        const hasFileAccess = res?.data?.has_access;
        const uuid = data.file_uuid;
        if (hasFileAccess) {
          const recently_viewed_body = {
            ...data,
            mapping_id: data?.report_id,
          };
          OpenSearchService.saveRecentlyViewedData(recently_viewed_body)
            .then((_res) => {})
            .catch((saveError) => {
              console.error("Failed to save recently viewed:", saveError);
            });
          HistoricalFileService.downloadHistoricalFile(uuid)
            .then((response) => {
              const type = response.headers["content-type"];
              // Create a blob from the response
              const blob = new Blob([response.data], {
                type,
              });

              // Create a temporary URL for the blob
              const url = window.URL.createObjectURL(blob);
              delay(() => {
                // Create a link element
                const link = document.createElement("a");
                link.href = url;

                // Set the file name (if provided in response headers)
                let filename = response.headers["content-disposition"]
                  ? response.headers["content-disposition"]
                      .split("filename=")[1]
                      .trim()
                      .replace(/['"]/g, "")
                  : "downloaded_file";

                filename = filename.replace(
                  constants.REG_EX.HISTORICAL_FILE_NAME_REG_EX,
                  ""
                );

                link.setAttribute("download", filename);

                // Append the link to the document
                document.body.appendChild(link);

                // Trigger the download
                link.click();

                // for GA EVENT
                gaEvent.sendEventToGA(
                  constants.GA.CUSTOM_EVENTS.DOWNLOAD_VIEW,
                  {
                    fileName: filename,
                    fileFormat: type,
                    moduleName:
                      constants.APPLICATION_ROUTE.HISTORICAL_FILE.NAME,
                    id: uuid,
                    pageUrl: location.pathname + location.search,
                  }
                );

                // Clean up by removing the link and revoking the object URL
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
              }, 500);
            })
            .catch((error) => {
              Toast(
                error?.data?.error
                  ? error.data.error
                  : constants.ERROR.SOMETHING_WENT_WRONG,
                "error"
              );
            });
        } else {
          // If no access, show info toast
          Toast("You don't have access to this report.", "INFO");
        }
      })
      .catch((error) => {
        Toast(
          error?.data?.error
            ? error.data.error
            : constants.ERROR.SOMETHING_WENT_WRONG,
          "error"
        );
      });
  };

  // Click handlers for history/suggestion
  const handleHistoryClick = (item) => {
    setFinalSearchTerm(item);
    setShowSearchSuggestion(false);
  };

  const handleSuggestionClick = (item) => {
    setFinalSearchTerm(item);
    setSearchInput(item);
    setShowSearchSuggestion(false);
  };

  const handleDateChange = (id, value) => {
    const formattedDate = moment(value).format("YYYY-MM-DD");
    if (id === "start") setStartDate(formattedDate);
    if (id === "end") setEndDate(formattedDate);
  };

  const getReportPath = (type, report_name, segment_name) => {
    let path =
      type === "company" ? "Company Research" : getReportTypeLabel(type);
    if (report_name) {
      path += " > " + report_name;
    }

    if (segment_name) {
      path += " > " + segment_name;
    }
    return path;
  };

  return (
    <aside className="main_content">
      <div className="globalSearch-div row">
        <div
          className="col-12 col-md-10 col-lg-10 mr-auto globalSearch-section pt-0"
          style={{ overflow: "hidden" }}
        >
          <div className="search-content-section">
            <div className="search-filter position-relative d-flex">
              <span
                className="material-icons-outlined back_arrow"
                onClick={goBackToDashboard}
              >
                arrow_back
              </span>
              <div className="w-100">
                <SearchBar
                  searchInput={searchInput}
                  setSearchInput={setSearchInput}
                  options={options}
                  selected={selectedReportType}
                  onCompanyResearchOptionsChange={
                    handleCompanyResearchOptionsChange
                  }
                  showSearchSuggestions={showSearchSuggestion}
                  searchHistory={searchHistory}
                  searchSuggestion={searchSuggestion}
                  onSearchFocus={() => setShowSearchSuggestion(true)}
                  onCloseModal={() => setShowSearchSuggestion(false)}
                  onHistoryClick={handleHistoryClick}
                  onSuggestionClick={handleSuggestionClick}
                  setShowSearchSuggestion={setShowSearchSuggestion}
                />
              </div>
              <span
                className="material-icons-outlined reset-search-input-icon"
                onClick={resetSearchInput}
              >
                autorenew
              </span>
            </div>
          </div>
          <div className="result-section-count-filters">
            <div className="result-count">
              <span className="fs14">RESULT</span>
              <span className="count ml-2 my-0 py-0 px-2">{resultCount}</span>
            </div>
            <div className="filters-open-search">
              {showDatePicker && (
                <div className="custom-date-range-option d-flex align-items-center justify-content-center">
                  <CustomDatePicker
                    className="custom_dates custom-date-combobox-list with-noborder with-shadow-sm"
                    id="start"
                    date={startdate}
                    onChangeHandler={handleDateChange}
                    max={endDate}
                    placeholder={"Start Date"}
                  />
                  <CustomDatePicker
                    className="custom_dates custom-date-combobox-list with-noborder with-shadow-sm"
                    id="end"
                    date={endDate}
                    onChangeHandler={handleDateChange}
                    max={moment().format("YYYY-MM-DD")}
                    placeholder={"End Date"}
                  />
                </div>
              )}
              <div className="published-date-filter">
                <CustomDropdownList
                  data={publishedDateOptions}
                  className="publish-date-dropdown custom-combobox-list"
                  value={selectedDateFilter}
                  handleChange={handleLastUpdatedChange}
                  placeholder="Published Date"
                  valueField="value"
                  textField="label"
                />
              </div>
              <div className="format-type-filter">
                <CustomDropdownList
                  data={filterByTypeOptions} // dropdown options data
                  className="format-file-dropdown custom-combobox-list"
                  value={filterByTypeFilter} // currently selected value
                  handleChange={handleFilterByTypeChange} // dropdown change handler
                  placeholder="Format type"
                  valueField="value"
                  textField="label"
                />
              </div>
              <div className="reset-span">
                <span onClick={handleResetPage}>reset </span>
              </div>
            </div>
          </div>
          <div
            className={
              searchResult?.length
                ? "global-search-result-list"
                : "no-results-found"
            }
          >
            {searchResult?.length ? (
              searchResult.map((item, idx) => {
                const data = item?._source;
                // Destructure commonly used fields
                const {
                  method,
                  type,
                  subcategory_title,
                  subcategory_name,
                  report_name,
                  content,
                  created_at,
                  segment_name,
                  files_name,
                } = data;

                // Determine type of content
                const isFile = method === "file";
                const isContent = method === "content";

                // Truncate subcategory title with fallback to subcategory name
                const truncatedSubCatTitle = subcategory_title?.trim()
                  ? CustomEllipsis(subcategory_title, 170)
                  : CustomEllipsis(subcategory_name, 170);

                // Truncate report name and content
                const truncatedFiletName = CustomEllipsis(files_name, 170);
                const truncatedContent = CustomEllipsis(content, 170);

                return (
                  <div
                    key={idx}
                    className="search-result-item result-card d-flex"
                  >
                    {/* Left icon section */}
                    <div className="icon-section">
                      {renderIcon(type, method)}
                    </div>

                    {/* Right content info section */}
                    <div className="ml-2 info-section">
                      {/* Title: Conditionally applies hover effect */}
                      <p
                        className={`title ${isFile ? "no-hover" : ""}`}
                        onClick={() => {
                          // Only trigger click if it's "content" type
                          if (isContent) checkFileAccess(data);
                        }}
                      >
                        {/* Show subcategory or report name based on method */}
                        {isContent
                          ? truncatedSubCatTitle.text
                          : truncatedFiletName.text}
                      </p>

                      {/* Description + download or date */}
                      <div className="desc-download">
                        {/* Truncated content with conditional hover */}
                        <p
                          className={`description ${isFile ? "no-hover" : ""}`}
                          onClick={() => {
                            // Only trigger click if it's "content" type
                            if (isContent) checkFileAccess(data);
                          }}
                        >
                          {truncatedContent.text}
                        </p>

                        {/* Conditional display: Download for file, Date for content */}
                        {isFile ? (
                          <span
                            className="download"
                            onClick={() => handleDownloadFile(data)}
                          >
                            Download
                          </span>
                        ) : (
                          <span className="date">
                            {created_at
                              ? moment(created_at).format("MMM D, YYYY")
                              : "—"}
                          </span>
                        )}
                      </div>

                      {/* Path text, dynamic based on type */}
                      <p className="path">
                        {getReportPath(type, report_name, segment_name)}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div>No search results found</div>
            )}
          </div>
          {searchResult.length > 0 && (
            <>
              <div className={"global-search-pagination pb-0"}>
                <CustomPagination
                  type="search"
                  className="center-pagination custom-pagination-new mr-1 mt-0"
                  handlePage={handlePage}
                  totalPages={totalPages}
                  handlePageSize={handlePageSize}
                  pageSize={pagination.pageSize}
                  pageNo={pagination.pageNo}
                  marginTop={false}
                />
              </div>
              <div className="global-search-legend-result my-1">
                <Legends />
              </div>
            </>
          )}
        </div>
      </div>
    </aside>
  );
};

export default GlobalSearchResult;
